import type { MountainCommand, ParsedMountainCommand } from "./MountainCommand.js";

const normalize = (input: string): string =>
  input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[¿?¡!.,;:]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const patterns: readonly { readonly command: MountainCommand; readonly phrases: readonly string[] }[] = [
  { command: "DISTANCE_REMAINING", phrases: ["cuanto falta", "cuantos kilometros faltan", "que distancia queda", "cuanto me queda"] },
  { command: "ROUTE_PROGRESS", phrases: ["que porcentaje llevo", "cuanto porcentaje llevo", "progreso de ruta", "que progreso llevo"] },
  { command: "DISTANCE_TRAVELLED", phrases: ["cuanto he recorrido", "cuanto recorri", "distancia recorrida", "cuantos kilometros llevo"] },
  { command: "ROUTE_DEVIATION", phrases: ["estoy fuera de ruta", "me desvie", "estoy desviado", "estoy sobre la ruta"] },
  { command: "CURRENT_ALTITUDE", phrases: ["a que altura estoy", "cual es mi altitud", "altura actual", "altitud actual"] },
  { command: "GPS_ACCURACY", phrases: ["que tan preciso esta el gps", "precision gps", "cual es la precision del gps", "error del gps"] },
  { command: "GPS_STATUS", phrases: ["tengo buena senal gps", "como esta el gps", "estado del gps", "gps esta bien"] },
  { command: "OFFLINE_CAPABILITIES", phrases: ["no tengo internet que puedo usar", "que funciona sin internet", "que puedo usar offline", "funciones offline"] },
  { command: "OFFLINE_STATUS", phrases: ["estoy offline", "tengo internet", "estoy sin conexion", "modo offline"] },
  { command: "AVAILABLE_DATA", phrases: ["que datos tienes disponibles", "que datos tienes", "datos disponibles", "que informacion tienes"] },
  { command: "ALERT_STATUS", phrases: ["hay alguna alerta", "tengo alertas", "hay alertas", "estado de alertas"] },
  { command: "CONTINUE_ASSESSMENT", phrases: [
  "puedo seguir",
  "puedo continuar",
  "es seguro continuar",
  "debo seguir",
  "es recomendable continuar",
  "es recomendable seguir",
  "me recomiendas continuar",
  "deberia continuar",
  "deberia seguir"
]},
  { command: "BASIC_RECOMMENDATION", phrases: ["que recomiendas", "dame una recomendacion", "que deberia hacer", "recomendacion"] },
  { command: "CURRENT_POSITION", phrases: ["cual es mi posicion", "donde estoy", "mis coordenadas", "posicion actual"] },
  { command: "TIME_REMAINING", phrases: ["cuanto tiempo falta", "cuanto tiempo me queda", "tiempo restante", "cuantos minutos faltan"] },
  { command: "ROUTE_STATUS", phrases: ["cual es mi estado de ruta", "estado de ruta", "resumen de ruta", "como voy en la ruta"] },

  { command: "COLD_ACTION", phrases: ["tengo frio que hago", "tengo mucho frio", "me estoy enfriando", "que hago con el frio"] },
  { command: "ADD_LAYER", phrases: ["debo ponerme otra capa", "me pongo otra capa", "necesito otra capa", "debo abrigarme mas"] },
  { command: "CLOTHING_NOW", phrases: ["que ropa conviene", "que ropa uso ahora", "como debo vestirme", "ropa para este momento"] },
  { command: "WET_CLOTHING", phrases: ["mi ropa esta mojada", "estoy mojado", "que hago con ropa mojada", "me moje"] },
  { command: "PRESERVE_HEAT", phrases: ["como conservar calor", "como mantengo el calor", "como evitar perder calor", "como no enfriarme"] },
  { command: "STRONG_WIND", phrases: ["hay mucho viento", "viento fuerte que hago", "como protegerme del viento", "que hago con viento fuerte"] },
  { command: "HEAVY_RAIN", phrases: ["esta lloviendo que hago", "lluvia fuerte que hago", "que hago con lluvia", "me estoy mojando por lluvia"] },
  { command: "STOP_BREAK", phrases: ["que hago durante una parada", "debo abrigarme al detenerme", "como hago una pausa", "que revisar en una pausa"] },
  { command: "NIGHTFALL", phrases: ["esta oscureciendo", "se hace de noche", "que hago si cae la noche", "me va a coger la noche"] },
  { command: "SAVE_BATTERY", phrases: ["como ahorro bateria", "tengo que ahorrar bateria", "me queda poca bateria", "como conservar bateria"] },
  { command: "HYDRATION", phrases: ["debo tomar agua", "cuando hidratarme", "necesito hidratarme", "como manejar el agua"] },
  { command: "FOOD_ENERGY", phrases: ["conviene comer algo", "tengo poca energia", "estoy cansado que hago", "debo comer"] },
  { command: "SUN_PROTECTION", phrases: ["hay mucho sol", "como protegerme del sol", "sol fuerte que hago", "que hago con el sol"] },
  { command: "BASIC_GEAR_CHECK", phrases: ["que equipo basico revisar", "que equipo debo revisar", "revisa mi equipo", "equipo basico"] },
  { command: "LOST_ROUTE", phrases: ["creo que estoy perdido", "no encuentro la ruta", "perdi la ruta", "estoy perdido"] },
  { command: "RETURN_CHECK", phrases: ["que revisar antes de regresar", "conviene regresar", "debo regresar", "revisar regreso"] },
  { command: "GPS_UNRELIABLE_ACTION", phrases: ["el gps esta mal que hago", "puedo confiar en esta posicion", "gps poco preciso que hago", "gps inestable"] },
  { command: "HIGH_ALTITUDE_GENERAL", phrases: ["estoy muy alto que debo tener en cuenta", "que considerar en altura", "consejo por altitud", "estoy a gran altitud"] },
  { command: "SLIPPERY_TERRAIN", phrases: ["terreno resbaloso", "el terreno esta muy resbaloso", "terreno dificil que hago", "suelo resbaloso"] },
  { command: "THUNDERSTORM_ACTION", phrases: ["escucho truenos", "hay tormenta electrica", "veo rayos", "que hago con tormenta"] },
  { command: "LOW_VISIBILITY", phrases: ["hay neblina", "no veo bien la ruta", "poca visibilidad", "hay mucha niebla"] },
  { command: "ORIENTATION_CHECK", phrases: ["que debo revisar antes de moverme", "que revisar antes de moverme", "como me oriento", "que revisar para orientarme", "orientacion basica"] },
  { command: "NEED_HELP", phrases: ["necesito ayuda", "tengo una emergencia", "como pido ayuda", "necesito asistencia"] },
  { command: "PRE_DEPARTURE_CHECK", phrases: ["que revisar antes de salir", "lista antes de salir", "preparacion antes de salir", "que llevo antes de salir"] },
];

export class CommandParser {
  parse(input: string): ParsedMountainCommand | null {
    const normalizedInput = normalize(input);
    if (!normalizedInput) return null;

    for (const entry of patterns) {
      if (entry.phrases.some((phrase) => normalizedInput.includes(phrase))) {
        return { command: entry.command, normalizedInput };
      }
    }

    return null;
  }
}
